import { Filter, getEmptyFilterExpression } from '@fabric-ui/components';

/**
 * Transforms backend filter format (rules/operator) to frontend standard Filter format.
 */
export const backendToFrontendFilter = (backendFilter: any, conditions: any[]): Filter => {
  const filterObj =
    typeof backendFilter === 'string'
      ? (() => {
          try {
            return JSON.parse(backendFilter);
          } catch {
            return null;
          }
        })()
      : backendFilter;

  if (!filterObj || (!filterObj.rules && !filterObj.field)) {
    return getEmptyFilterExpression();
  }

  const transformRule = (rule: any): any => {
    if (!rule) return null;

    // Handle nested rules (logical groups)
    if (rule.rules && Array.isArray(rule.rules)) {
      const operator = rule.operator?.toLowerCase() === 'or' ? 'or' : 'and';
      const operands = rule.rules.map(transformRule).filter(Boolean);

      if (operands.length === 0) return null;

      // Flatten single-operand logical groups to reduce nesting,
      // but keep the root structure standard
      if (operands.length === 1 && operands[0].expression) {
        return operands[0];
      }

      return {
        [operator]: { operands },
      };
    }

    // Handle leaf rules (individual conditions)
    if (rule.field) {
      const conditionCatalog = conditions.find(c => c.fieldKey === rule.field);
      const fieldType = conditionCatalog?.fieldType?.toLowerCase();
      const { value } = rule;

      const getConditionKey = () => {
        // Use standard condition keys even for CHANGE operator to maintain compatibility with NewExpressionBuilder
        if (fieldType) {
          if (['number', 'int', 'double', 'float', 'long'].includes(fieldType)) {
            return 'numberCondition';
          }
          if (['boolean', 'bool'].includes(fieldType)) {
            return 'boolCondition';
          }
        } else if (typeof value === 'number') {
          return 'numberCondition';
        } else if (typeof value === 'boolean' || value === 'true' || value === 'false') {
          return 'boolCondition';
        }
        return 'stringCondition';
      };

      const conditionKey = getConditionKey();

      const operatorMap: Record<string, string> = {
        EQUALS: 'equals',
        NOT_EQUALS: 'notEquals',
        CONTAINS: 'contains',
        NOT_CONTAINS: 'notContains',
        GREATER_THAN: 'gt',
        GREATER_THAN_OR_EQUALS: 'gte',
        LESS_THAN: 'lt',
        LESS_THAN_OR_EQUALS: 'lte',
        STARTS_WITH: 'startsWith',
        ENDS_WITH: 'endsWith',
        IN: 'in',
        NOT_IN: 'notIn',
        EMPTY: 'empty',
        NOT_EMPTY: 'notEmpty',
        CHANGE: 'change',
      };

      const operator = operatorMap[rule.operator] || 'equals';

      const getProcessedValue = () => {
        if (operator === 'change') {
          return { from: rule.from || '', to: rule.to || '' };
        }
        if (['in', 'notIn'].includes(operator)) {
          return { values: Array.isArray(value) ? value : [value] };
        }
        if (['empty', 'notEmpty'].includes(operator)) {
          return {};
        }
        return value;
      };

      const processedValue = getProcessedValue();

      return {
        expression: {
          fieldName: rule.field,
          [conditionKey]: {
            [operator]: processedValue,
          },
        },
      };
    }

    return null;
  };

  const result = transformRule(filterObj);

  // Root logical operator check
  if (!result) return getEmptyFilterExpression();

  if (result.expression) {
    return { and: { operands: [result] } };
  }

  // Ensure result has operands array even if empty
  const operator = result.and ? 'and' : 'or';
  if (result[operator] && !result[operator].operands) {
    return {
      ...result,
      [operator]: {
        ...result[operator],
        operands: [],
      },
    };
  }

  return result;
};

/**
 * Transforms frontend Filter format to backend rules/operator format.
 */
export const frontendToBackendFilter = (filter: Filter): any => {
  const transformOperand = (operand: any): any => {
    if (!operand) return null;

    // Handle logical groups
    if (operand.and || operand.or) {
      const operator = operand.and ? 'AND' : 'OR';
      const opObj = operand.and || operand.or;
      const rules = opObj.operands.map(transformOperand).filter(Boolean);

      if (rules.length === 0) return null;

      return { rules, operator };
    }

    // Handle expressions (leaf nodes)
    if (operand.expression) {
      const { fieldName, ...conditions } = operand.expression;
      if (!fieldName) return null;

      const conditionType = Object.keys(conditions)[0];
      const conditionValue = conditions[conditionType];
      if (!conditionValue) return null;

      const operatorKey = Object.keys(conditionValue)[0];
      const value = conditionValue[operatorKey];

      const operatorMap: Record<string, string> = {
        equals: 'EQUALS',
        notEquals: 'NOT_EQUALS',
        contains: 'CONTAINS',
        notContains: 'NOT_CONTAINS',
        gt: 'GREATER_THAN',
        gte: 'GREATER_THAN_OR_EQUALS',
        lt: 'LESS_THAN',
        lte: 'LESS_THAN_OR_EQUALS',
        startsWith: 'STARTS_WITH',
        endsWith: 'ENDS_WITH',
        in: 'IN',
        notIn: 'NOT_IN',
        empty: 'EMPTY',
        notEmpty: 'NOT_EMPTY',
        change: 'CHANGE',
      };

      const backendOperator = operatorMap[operatorKey] || 'EQUALS';

      const getProcessedValue = () => {
        // Extract values array for list operators
        if (['IN', 'NOT_IN'].includes(backendOperator) && value && typeof value === 'object' && 'values' in value) {
          return value.values;
        }
        if (['EMPTY', 'NOT_EMPTY'].includes(backendOperator)) {
          return null;
        }
        if (backendOperator === 'CHANGE') {
          return null; // Handled by from/to extraction
        }
        return value;
      };

      const processedValue = getProcessedValue();

      return {
        field: fieldName,
        operator: backendOperator,
        value: processedValue,
        ...(backendOperator === 'CHANGE' && {
          from: value?.from || '',
          to: value?.to || '',
        }),
      };
    }

    return null;
  };

  const result = transformOperand(filter);

  // Ensure root is always an object with rules and operator
  if (result && result.field) {
    return { rules: [result], operator: 'AND' };
  }

  return result || { rules: [], operator: 'AND' };
};
